import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { SURVEY_NAME_LIMIT } from 'shared/constants/limits'
import { toastStore } from 'shared/ui'
import { BlockerStore } from 'shared/ui/Blocker'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IParamsCreateSurvey,
  type IResponseFullSurvey,
  type ISurveySettings,
  SurveysStatus,
} from 'entities/Surveys/api/types'
import { SurveysApi } from 'entities/Surveys'
import { SurveysPath } from 'entities/ExportRouters'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { openDisableSurveyModal } from 'pages/surveys/ui/actions/disableSurvey/openDisableSurveyModal'
import { logActivateSurveyEvent } from 'pages/surveys/lib/logSurveyStatusEvents'
import { SurveyEnrolmentStore } from 'pages/surveys/ui/SurveyEnrolment'
import { SurveyEnrollContactsStore } from 'pages/surveys/ui/SurveyView/ui/SurveyEnrollContacts/store/surveyEnrollContactsStore'
import { NpsQuestionStore } from 'pages/surveys/ui/SurveyView/ui/NpsQuestion/store/npsQuestionStore'
import { FallbackActionsStore } from 'pages/surveys/ui/SurveyView/ui/FallbackActions/store/fallbackActionsStore'
import { FallbackAgentActionsStore } from 'pages/surveys/ui/SurveyView/ui/FallbackActions/store/fallbackAgentActionsStore'
import { ScoreElaborationStore } from 'pages/surveys/ui/SurveyView/ui/ScoreElaboration/store/scoreElaborationStore'
import { ClosingMessageStore } from 'pages/surveys/ui/SurveyView/ui/ClosingMessage/store/closingMessageStore'
import { SuccessActionsStore } from 'pages/surveys/ui/SurveyView/ui/SuccessActions/store/successActionsStore'
import { type IPreviewBubble } from 'pages/surveys/ui/SurveyView/ui/Preview/Preview'

export class SurveyCreateStore {
  id: number | null = null
  isActive = false
  status: SurveysStatus | null = null
  nameInputStore: NameInputStore
  enrollContactsStore: SurveyEnrollContactsStore
  npsQuestionStore: NpsQuestionStore
  fallbackActionsStore: FallbackActionsStore
  fallbackAgentActionsStore: FallbackAgentActionsStore
  scoreElaborationStore: ScoreElaborationStore
  closingMessageStore: ClosingMessageStore
  successActionsStore: SuccessActionsStore
  errorsMap = new Map<string, string>()
  isSaving = false
  blockerStore: BlockerStore
  isDuplicate = false
  private _originSettings: ISurveySettings | null = null
  private _blockerConfig: {
    title: string
    primaryText: string
    primaryAction: () => Promise<unknown>
    additionalSecondaryActionText?: string
    modalType: ModalTypeList
  } = {
    title: '',
    primaryText: '',
    primaryAction: async () => this.onSaveSurvey({ withError: true }),
    modalType: ModalTypeList.WARNING,
  }

  private _syncBlockerConfig = () => {
    if (this.isActive) {
      this._blockerConfig.title = 'Publish survey before closing?'
      this._blockerConfig.primaryText = 'Publish changes'
      this._blockerConfig.primaryAction = async () =>
        this.onActivateSurvey({ withError: true, skipNavigation: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't publish"
    } else {
      this._blockerConfig.title = 'Save survey before closing?'
      this._blockerConfig.primaryText =
        this.status === SurveysStatus.Draft || this.status === null
          ? 'Save as draft'
          : 'Save changes'
      this._blockerConfig.primaryAction = async () => this.onSaveSurvey({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't save"
    }
  }

  constructor(
    readonly entities: IEntitiesStore,
    private _surveyId: number | undefined,
    isDuplicate?: boolean
  ) {
    this.nameInputStore = new NameInputStore({
      focusOnMount: !_surveyId || isDuplicate,
      isResetErrorOnChange: true,
      limit: SURVEY_NAME_LIMIT,
    })
    this.enrollContactsStore = new SurveyEnrollContactsStore(entities)
    this.npsQuestionStore = new NpsQuestionStore(entities.organizationStore.name)
    this.fallbackActionsStore = new FallbackActionsStore()
    this.fallbackAgentActionsStore = new FallbackAgentActionsStore(entities)
    this.scoreElaborationStore = new ScoreElaborationStore(entities)
    this.closingMessageStore = new ClosingMessageStore(entities)
    this.successActionsStore = new SuccessActionsStore(entities)
    if (_surveyId && !isDuplicate) this.id = _surveyId
    this.isDuplicate = isDuplicate ?? false

    makeAutoObservable(this)
    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  get isEditMode() {
    return Boolean(this.id) && !this.isDuplicate
  }

  init = async (response: IResponseFullSurvey) => {
    if (this.isDuplicate) {
      this.isActive = false
      this.status = null
    } else {
      this.isActive = response.is_active
      this.status = response.status
    }

    this._originSettings = response.settings ?? null
    this.nameInputStore.init(this.isDuplicate ? `Copy of ${response.name}` : response.name)
    this.npsQuestionStore.setCompanyName(response.question?.company_name ?? '')
    this.fallbackActionsStore.syncOrigin(response.settings)
    this.fallbackAgentActionsStore.init(response.settings?.fallback_action?.value)
    this.scoreElaborationStore.init(response.settings)
    this.closingMessageStore.init(response.settings)
    this.successActionsStore.init(response.settings)
    try {
      await this.enrollContactsStore.init(response)
    } catch (e) {
      logger.error(e)
    } finally {
      this._syncBlockerConfig()
      this.blockerStore.updateCache()
    }
  }

  get previewBubbles(): IPreviewBubble[] {
    const bubbles: IPreviewBubble[] = [...this.npsQuestionStore.previewBubble]
    const showScoreElaboration =
      this.scoreElaborationStore.enabled && this.scoreElaborationStore.hasPreviewContent
    if (showScoreElaboration) {
      bubbles.push({
        id: 'score-elaboration',
        direction: 'outbound',
        html: this.scoreElaborationStore.previewHtml,
        attachments: this.scoreElaborationStore.previewAttachments,
      })
    }
    if (this.closingMessageStore.enabled) {
      if (showScoreElaboration) {
        bubbles.push({
          id: 'closing-message-response',
          direction: 'inbound',
          parts: [{ text: 'Customer’s response' }],
        })
      }
      if (this.closingMessageStore.hasPreviewContent) {
        bubbles.push({
          id: 'closing-message',
          direction: 'outbound',
          html: this.closingMessageStore.previewHtml,
          attachments: this.closingMessageStore.previewAttachments,
        })
      }
    }
    return bubbles
  }

  get params(): IParamsCreateSurvey {
    const empty = { message: '', media_url: [] }

    const baseSettings: ISurveySettings = this._originSettings ?? {
      allow_re_entering: { value: false },
      score_elaboration: { value: false },
      score_elaboration_message: { value: empty },
      closing_message: { value: false },
      closing_message_value: { value: empty },
      success_action: { value: null },
      fallback_action: { value: null },
    }

    const settings: ISurveySettings = {
      ...baseSettings,
      allow_re_entering: {
        value: this.enrollContactsStore.enrollmentStore.isContactReEnroll,
      },
      ...this.scoreElaborationStore.params,
      ...this.closingMessageStore.params,
      ...this.successActionsStore.params,
      fallback_action: { value: this.fallbackAgentActionsStore.paramsValue },
      ...this.fallbackActionsStore.params,
    }

    return {
      name: this.nameInputStore.name,
      type: 1,
      is_active: false,
      question: {
        company_name: this.npsQuestionStore.companyName,
        text: this.npsQuestionStore.text,
      },
      filters: this.enrollContactsStore.paramsFilters,
      exclude_filters: this.enrollContactsStore.paramsExcludeFilters,
      send_from: this.enrollContactsStore.numberStore.paramsSendFrom,
      settings,
    }
  }

  setError = (key: string, error: string) => {
    this.errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this.errorsMap.delete(key)
  }

  onSaveSurvey = async (props?: { withError?: true }) => {
    const withError = props?.withError

    if (!this.validateName()) {
      if (withError) throw new Error('Validation failed')
      return
    }
    this.isSaving = true

    try {
      if (this.id) {
        await SurveysApi.updateSurvey(this.id, this.params)
        this.blockerStore.updateCache()
      } else {
        const { data } = await SurveysApi.createSurvey(this.params)
        this.blockerStore.updateCache()
        uiStore.changeRoute({ path: SurveysPath.edit(data.id) })
      }

      toastStore.add({
        title: 'Survey saved',
        type: 'info',
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error)
      logger.error(error)

      if (withError) throw error
      return null
    } finally {
      this.isSaving = false
    }
  }

  onActivateSurvey = async (props?: { withError?: true; skipNavigation?: boolean }) => {
    if (!this.checkValidate()) {
      if (props?.withError) throw new Error('Validation failed')
      return
    }
    this.isSaving = true

    const isPublished = this.isActive
    const payload = { ...this.params, is_active: true }
    try {
      let surveyId = this.id
      if (this.id) {
        await SurveysApi.updateSurvey(this.id, payload)
      } else {
        const { data } = await SurveysApi.createSurvey(payload)
        surveyId = data.id
      }

      if (!isPublished) logActivateSurveyEvent(this.entities)

      this.isActive = true
      this.status = SurveysStatus.Active
      this._syncBlockerConfig()

      this.blockerStore.updateCache()

      const navigate = () => {
        if (!props?.skipNavigation) {
          uiStore.changeRoute({ path: `${SurveysPath.root}` })
        }
      }

      if (!surveyId) {
        navigate()
        return
      }

      const surveyEnrolmentStore = new SurveyEnrolmentStore()
      await surveyEnrolmentStore.init({
        data: {
          name: this.nameInputStore.name,
          id: surveyId,
          filters: this.params.filters,
          exclude_filters: this.params.exclude_filters,
          isPublished,
        },
        onNext: navigate,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        this._syncErrors(error)

        if (error.response?.status === 500) {
          toastStore.add({
            title: 'Something went wrong',
            desc: 'Please try again',
            type: 'error',
          })
        }
      }
      logger.error(error)
      if (props?.withError) throw error
    } finally {
      this.isSaving = false
    }
  }

  private _syncErrors = (e: AxiosError) => {
    const sendFromError = e.errors.getError('send_from') || e.errors.getError('send_from.options')
    const nameError = e.errors.getError('name')
    const companyNameError = e.errors.getError('question.company_name')

    if (sendFromError) this.enrollContactsStore.numberStore.setError(sendFromError)
    if (nameError) this.nameInputStore.setError(nameError)
    if (companyNameError) this.npsQuestionStore.setError(companyNameError)

    this.scoreElaborationStore.syncApiErrors(e.errors)
    this.closingMessageStore.syncApiErrors(e.errors)
    this.successActionsStore.syncApiErrors(e.errors)
    this.fallbackAgentActionsStore.syncApiErrors(e.errors)
  }

  validateName = () => {
    if (!this.nameInputStore.name) {
      this.nameInputStore.setError('Please enter survey name')
      return false
    }
    return true
  }

  checkValidate = () => {
    let isValid = true

    if (!this.validateName()) {
      isValid = false
    }

    if (!this.npsQuestionStore.companyName) {
      this.npsQuestionStore.setError('Please enter company or product name')
      isValid = false
    }

    if (!this.scoreElaborationStore.validate()) {
      isValid = false
    }

    if (!this.closingMessageStore.validate()) {
      isValid = false
    }

    return isValid
  }

  disableSurvey = () => {
    if (!this.id) return

    openDisableSurveyModal(this.id, this.entities, (data) => {
      this.isActive = data.is_active
      this.status = data.status
      this._syncBlockerConfig()
    })
  }

  dispose = () => {
    this.enrollContactsStore.dispose()
    this.scoreElaborationStore.dispose()
    this.closingMessageStore.dispose()
    this.successActionsStore.dispose()
    this.fallbackAgentActionsStore.dispose()
  }
}
